<template>
  <div class="slide slide-chart-callouts">
    <!-- Left Panel: Chart -->
    <div class="chart-panel">
      <h2 class="chart-title">{{ data.title }}</h2>

      <!-- Bar Chart - Card Layout -->
      <div class="chart-container">
        <div class="chart-cards">
          <div
            v-for="(item, index) in chartItems"
            :key="index"
            class="metric-card"
            :class="`card-${item.color}`"
            :style="{ animationDelay: `${index * 0.08}s` }"
          >
            <div class="metric-header">
              <span class="metric-label">{{ item.name }}</span>
              <span class="metric-change" :class="`text-${item.color}`">{{ formatChange(item.change) }}</span>
            </div>
            <div class="metric-bar-track">
              <div
                class="metric-bar-fill"
                :class="`bg-${item.color}`"
                :style="{ width: `${Math.min(Math.abs(item.change) * 2, 100)}%` }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Panel: Callouts -->
    <div class="callouts-panel">
      <div class="callouts-header">
        <span class="mono-label">KEY INSIGHTS</span>
      </div>

      <div class="callouts-list">
        <!-- Improvements -->
        <div class="callout-section" v-if="data.improvements && data.improvements.length">
          <h3 class="section-label positive">Improvements</h3>
          <div
            v-for="(item, index) in data.improvements"
            :key="`imp-${index}`"
            class="callout-item positive"
          >
            <div class="callout-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 5L5 12H9V19H15V12H19L12 5Z" fill="currentColor"/>
              </svg>
            </div>
            <div class="callout-content">
              <span class="callout-metric">{{ item.name }}</span>
              <span class="callout-change">{{ formatChange(item.change) }}</span>
            </div>
          </div>
        </div>

        <!-- Declines -->
        <div class="callout-section" v-if="data.declines && data.declines.length">
          <h3 class="section-label negative">Areas of Concern</h3>
          <div
            v-for="(item, index) in data.declines"
            :key="`dec-${index}`"
            class="callout-item negative"
          >
            <div class="callout-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 19L19 12H15V5H9V12H5L12 19Z" fill="currentColor"/>
              </svg>
            </div>
            <div class="callout-content">
              <span class="callout-metric">{{ item.name }}</span>
              <span class="callout-change">{{ formatChange(item.change) }}</span>
            </div>
          </div>
        </div>

        <!-- Insights -->
        <div class="insights-list" v-if="data.insights && data.insights.length">
          <div
            v-for="(insight, index) in data.insights"
            :key="`insight-${index}`"
            class="insight-item"
          >
            <span class="bullet"></span>
            <span class="insight-text">{{ insight }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChartWithCalloutsSlide',
  props: {
    data: {
      type: Object,
      required: true
    },
    slideId: {
      type: String,
      default: ''
    }
  },
  computed: {
    chartItems() {
      const items = [];

      if (this.data.improvements) {
        this.data.improvements.forEach(item => {
          items.push({
            name: item.name,
            change: item.change,
            color: 'emerald'
          });
        });
      }

      if (this.data.declines) {
        this.data.declines.forEach(item => {
          items.push({
            name: item.name,
            change: item.change,
            color: 'rose'
          });
        });
      }

      // Sort by absolute change value
      return items.sort((a, b) => Math.abs(b.change) - Math.abs(a.change));
    }
  },
  methods: {
    formatChange(change) {
      const prefix = change > 0 ? '+' : '';
      return `${prefix}${change}`;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/quarterly-slides.scss';

.slide-chart-callouts {
  @extend .slide-base;
  background: $paper;
  display: flex;
}

.chart-panel {
  width: 60%;
  padding: 80px;
  border-right: 1px solid rgba($ink, 0.1);
}

.chart-title {
  @extend .serif-headline;
  font-size: 48px;
  color: $ink;
  margin: 0 0 60px;
}

.chart-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.chart-cards {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.metric-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px 28px;
  box-shadow: $shadow-soft;
  animation: fadeInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) backwards;
  transition: all 0.3s ease;
  border-left: 4px solid transparent;

  &:hover {
    transform: translateX(4px);
    box-shadow: $shadow-medium;
  }

  &.card-emerald {
    border-left-color: $emerald;
  }

  &.card-rose {
    border-left-color: $rose;
  }

  &.card-amber {
    border-left-color: $amber;
  }
}

.metric-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 16px;
}

.metric-label {
  font-family: $font-body;
  font-size: 17px;
  font-weight: 500;
  color: $ink;
  line-height: 1.4;
  flex: 1;
}

.metric-change {
  font-family: $font-mono;
  font-size: 24px;
  font-weight: 700;
  flex-shrink: 0;

  &.text-emerald { color: $emerald; }
  &.text-rose { color: $rose; }
  &.text-amber { color: $amber; }
}

.metric-bar-track {
  height: 8px;
  background: rgba($ink, 0.08);
  border-radius: 4px;
  overflow: hidden;
}

.metric-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.8s cubic-bezier(0.16, 1, 0.3, 1);

  &.bg-emerald { background: $emerald; }
  &.bg-rose { background: $rose; }
  &.bg-amber { background: $amber; }
}

.callouts-panel {
  width: 40%;
  padding: 80px 60px;
  background: rgba($ink, 0.02);
}

.callouts-header {
  margin-bottom: 40px;

  .mono-label {
    @extend .mono-label;
    font-size: 14px;
    color: $ink-subtle;
    letter-spacing: 0.1em;
  }
}

.callouts-list {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.callout-section {
  .section-label {
    font-family: $font-body;
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 16px;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    &.positive { color: $emerald; }
    &.negative { color: $rose; }
  }
}

.callout-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #fff;
  border-radius: 12px;
  margin-bottom: 12px;
  box-shadow: $shadow-soft;

  &.positive {
    border-left: 3px solid $emerald;

    .callout-icon { color: $emerald; }
    .callout-change { color: $emerald; }
  }

  &.negative {
    border-left: 3px solid $rose;

    .callout-icon { color: $rose; }
    .callout-change { color: $rose; }
  }
}

.callout-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba($ink, 0.05);
}

.callout-content {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.callout-metric {
  font-family: $font-body;
  font-size: 16px;
  font-weight: 600;
  color: $ink;
}

.callout-change {
  font-family: $font-mono;
  font-size: 16px;
  font-weight: 700;
}

.insights-list {
  margin-top: 24px;
}

.insight-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.bullet {
  width: 6px;
  height: 6px;
  background: $violet-500;
  border-radius: 50%;
  margin-top: 8px;
  flex-shrink: 0;
}

.insight-text {
  font-family: $font-body;
  font-size: 16px;
  color: $ink-muted;
  line-height: 1.5;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
